import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  CardActions,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';

import {
  Assessment as AssessmentIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  Timeline as TimelineIcon,
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  TableChart as TableChartIcon,
  Code as CodeIcon,
  Language as LanguageIcon,
  FilterList as FilterListIcon,
  Save as SaveIcon,
  PlayArrow as PlayArrowIcon
} from '@mui/icons-material';

import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';

import { useAuth } from '../contexts/AuthContext';

interface ComprehensiveMetrics {
  basic_metrics: {
    customers: {
      total: number;
      active: number;
      activation_rate: number;
    };
    check_ins: {
      total: number;
      successful: number;
      missed: number;
      success_rate: number;
    };
    alerts: {
      total: number;
      active: number;
      escalated: number;
      escalation_rate: number;
    };
    caregivers: {
      total: number;
    };
  };
  advanced_metrics: {
    [key: string]: {
      value: number;
      previous_value: number | null;
      change_percent: number | null;
      trend_direction: string;
      confidence_interval: [number, number];
      seasonality_score: number;
      anomaly_score: number;
      benchmark_percentile: number;
      significance_level: number;
    };
  };
  trend_analysis: {
    daily_trends: Array<{
      date: string;
      total_check_ins: number;
      successful_check_ins: number;
      success_rate: number;
    }>;
    trend_slope: number;
    trend_strength: number;
    granularity: string;
  };
  anomalies: Array<{
    date: string;
    metric: string;
    value: number;
    expected_range: [number, number];
    z_score: number;
    severity: string;
    description: string;
  }>;
  insights: Array<{
    type: string;
    metric: string;
    severity: string;
    description: string;
    recommendation: string;
    action_items: string[];
  }>;
  generated_at: string;
}

interface ExportFormat {
  value: string;
  label: string;
  description: string;
  supports_charts: boolean;
  supports_styling: boolean;
  file_extension: string;
}

interface ChartType {
  value: string;
  label: string;
  description: string;
}

interface ExportResult {
  success: boolean;
  filename: string;
  file_size: number;
  mime_type: string;
  download_url: string;
}

const AdvancedReportsDashboard: React.FC = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [metrics, setMetrics] = useState<ComprehensiveMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Date range state
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [granularity, setGranularity] = useState<string>('day');
  
  // Export dialog state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormats, setExportFormats] = useState<ExportFormat[]>([]);
  const [chartTypes, setChartTypes] = useState<ChartType[]>([]);
  const [selectedFormat, setSelectedFormat] = useState<string>('pdf');
  const [includeCharts, setIncludeCharts] = useState<boolean>(true);
  const [includeSummary, setIncludeSummary] = useState<boolean>(true);
  const [selectedChartType, setSelectedChartType] = useState<string>('line');
  const [reportTitle, setReportTitle] = useState<string>('Comprehensive Report');
  const [exporting, setExporting] = useState<boolean>(false);
  
  // Schedule dialog state
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleConfig, setScheduleConfig] = useState({
    name: '',
    frequency: 'weekly',
    recipients: '',
    format: 'pdf'
  });

  useEffect(() => {
    // Set default date range (last 30 days)
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    
    setEndDate(end.toISOString().split('T')[0]);
    setStartDate(start.toISOString().split('T')[0]);
    
    loadAvailableFormats();
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      loadComprehensiveMetrics();
    }
  }, [startDate, endDate, granularity]);

  const loadAvailableFormats = async () => {
    try {
      const response = await fetch('/api/v1/advanced-reports/available-formats', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setExportFormats(data.formats);
        setChartTypes(data.chart_types);
      }
    } catch (error) {
      console.error('Error loading available formats:', error);
    }
  };

  const loadComprehensiveMetrics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `/api/v1/advanced-reports/comprehensive-metrics?start_date=${startDate}&end_date=${endDate}&granularity=${granularity}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setMetrics(data);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to load metrics');
      }
    } catch (error) {
      setError('Network error occurred');
      console.error('Error loading metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    
    try {
      const response = await fetch(
        `/api/v1/advanced-reports/export/comprehensive-report?start_date=${startDate}&end_date=${endDate}&format=${selectedFormat}&include_charts=${includeCharts}&include_summary=${includeSummary}&chart_type=${selectedChartType}&report_title=${encodeURIComponent(reportTitle)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        const result: ExportResult = await response.json();
        
        // Download the file
        const downloadResponse = await fetch(result.download_url, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (downloadResponse.ok) {
          const blob = await downloadResponse.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = result.filename;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }
        
        setExportDialogOpen(false);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Export failed');
      }
    } catch (error) {
      setError('Export failed');
      console.error('Error exporting report:', error);
    } finally {
      setExporting(false);
    }
  };

  const handleScheduleReport = async () => {
    try {
      const response = await fetch('/api/v1/advanced-reports/schedule-report', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...scheduleConfig,
          start_date: startDate,
          end_date: endDate
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        alert(`Report scheduled successfully! Schedule ID: ${result.schedule_id}`);
        setScheduleDialogOpen(false);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to schedule report');
      }
    } catch (error) {
      setError('Failed to schedule report');
      console.error('Error scheduling report:', error);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getTrendColor = (direction: string) => {
    switch (direction.toLowerCase()) {
      case 'up': return 'success';
      case 'down': return 'error';
      default: return 'default';
    }
  };

  const formatPercentage = (value: number) => `${value.toFixed(1)}%`;
  const formatNumber = (value: number) => value.toLocaleString();

  if (loading && !metrics) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Advanced Reports Dashboard
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Date Range Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth>
              <InputLabel>Granularity</InputLabel>
              <Select
                value={granularity}
                onChange={(e) => setGranularity(e.target.value)}
                label="Granularity"
              >
                <MenuItem value="hour">Hour</MenuItem>
                <MenuItem value="day">Day</MenuItem>
                <MenuItem value="week">Week</MenuItem>
                <MenuItem value="month">Month</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                onClick={loadComprehensiveMetrics}
                disabled={loading}
                startIcon={<RefreshIcon />}
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                onClick={() => setExportDialogOpen(true)}
                startIcon={<DownloadIcon />}
              >
                Export
              </Button>
              <Button
                variant="outlined"
                onClick={() => setScheduleDialogOpen(true)}
                startIcon={<ScheduleIcon />}
              >
                Schedule
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {metrics && (
        <>
          {/* Tabs */}
          <Paper sx={{ mb: 3 }}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
              <Tab label="Overview" icon={<AssessmentIcon />} />
              <Tab label="Trends" icon={<TrendingUpIcon />} />
              <Tab label="Anomalies" icon={<WarningIcon />} />
              <Tab label="Insights" icon={<CheckCircleIcon />} />
              <Tab label="Benchmarks" icon={<BarChartIcon />} />
            </Tabs>
          </Paper>

          {/* Overview Tab */}
          {activeTab === 0 && (
            <Grid container spacing={3}>
              {/* Basic Metrics */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      System Overview
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          Total Customers
                        </Typography>
                        <Typography variant="h4">
                          {formatNumber(metrics.basic_metrics.customers.total)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          Active Customers
                        </Typography>
                        <Typography variant="h4">
                          {formatNumber(metrics.basic_metrics.customers.active)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          Check-in Success Rate
                        </Typography>
                        <Typography variant="h4">
                          {formatPercentage(metrics.basic_metrics.check_ins.success_rate)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          Active Alerts
                        </Typography>
                        <Typography variant="h4">
                          {formatNumber(metrics.basic_metrics.alerts.active)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Advanced Metrics */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Advanced Analytics
                    </Typography>
                    {Object.entries(metrics.advanced_metrics).map(([key, metric]) => (
                      <Box key={key} sx={{ mb: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">
                            {key.replace('_', ' ').toUpperCase()}
                          </Typography>
                          <Chip
                            label={metric.trend_direction.toUpperCase()}
                            color={getTrendColor(metric.trend_direction) as any}
                            size="small"
                          />
                        </Box>
                        <Typography variant="h6">
                          {metric.value.toFixed(2)}
                        </Typography>
                        {metric.change_percent && (
                          <Typography variant="body2" color="textSecondary">
                            {metric.change_percent > 0 ? '+' : ''}{metric.change_percent.toFixed(1)}% from previous period
                          </Typography>
                        )}
                        <LinearProgress
                          variant="determinate"
                          value={metric.benchmark_percentile}
                          sx={{ mt: 1 }}
                        />
                        <Typography variant="caption" color="textSecondary">
                          Benchmark: {metric.benchmark_percentile.toFixed(1)}%
                        </Typography>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Trends Tab */}
          {activeTab === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Check-in Success Rate Trend
                    </Typography>
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart data={metrics.trend_analysis.daily_trends}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip />
                        <Line type="monotone" dataKey="success_rate" stroke="#8884d8" />
                      </LineChart>
                    </ResponsiveContainer>
                    <Box mt={2}>
                      <Typography variant="body2">
                        Trend Slope: {metrics.trend_analysis.trend_slope.toFixed(3)}
                      </Typography>
                      <Typography variant="body2">
                        Trend Strength: {metrics.trend_analysis.trend_strength.toFixed(3)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Anomalies Tab */}
          {activeTab === 2 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Detected Anomalies ({metrics.anomalies.length})
                    </Typography>
                    {metrics.anomalies.length > 0 ? (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell>Metric</TableCell>
                              <TableCell>Value</TableCell>
                              <TableCell>Expected Range</TableCell>
                              <TableCell>Z-Score</TableCell>
                              <TableCell>Severity</TableCell>
                              <TableCell>Description</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {metrics.anomalies.map((anomaly, index) => (
                              <TableRow key={index}>
                                <TableCell>{anomaly.date}</TableCell>
                                <TableCell>{anomaly.metric}</TableCell>
                                <TableCell>{anomaly.value.toFixed(2)}</TableCell>
                                <TableCell>
                                  ({anomaly.expected_range[0].toFixed(1)}, {anomaly.expected_range[1].toFixed(1)})
                                </TableCell>
                                <TableCell>{anomaly.z_score.toFixed(2)}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={anomaly.severity.toUpperCase()}
                                    color={getSeverityColor(anomaly.severity) as any}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>{anomaly.description}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        No anomalies detected in the selected time period.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Insights Tab */}
          {activeTab === 3 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Actionable Insights ({metrics.insights.length})
                    </Typography>
                    {metrics.insights.length > 0 ? (
                      <List>
                        {metrics.insights.map((insight, index) => (
                          <ListItem key={index} divider>
                            <ListItemIcon>
                              <Chip
                                label={insight.severity.toUpperCase()}
                                color={getSeverityColor(insight.severity) as any}
                                size="small"
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={insight.type.replace('_', ' ').toUpperCase()}
                              secondary={
                                <Box>
                                  <Typography variant="body2" sx={{ mb: 1 }}>
                                    {insight.description}
                                  </Typography>
                                  <Typography variant="body2" color="primary">
                                    <strong>Recommendation:</strong> {insight.recommendation}
                                  </Typography>
                                  {insight.action_items && insight.action_items.length > 0 && (
                                    <Box mt={1}>
                                      <Typography variant="body2" color="textSecondary">
                                        <strong>Action Items:</strong>
                                      </Typography>
                                      <List dense>
                                        {insight.action_items.map((item, itemIndex) => (
                                          <ListItem key={itemIndex} sx={{ py: 0 }}>
                                            <ListItemText
                                              primary={item}
                                              primaryTypographyProps={{ variant: 'body2' }}
                                            />
                                          </ListItem>
                                        ))}
                                      </List>
                                    </Box>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        No insights generated for the selected time period.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Benchmarks Tab */}
          {activeTab === 4 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Performance Benchmarks
                    </Typography>
                    <Grid container spacing={2}>
                      {Object.entries(metrics.advanced_metrics).map(([key, metric]) => (
                        <Grid item xs={12} md={6} key={key}>
                          <Paper sx={{ p: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                              {key.replace('_', ' ').toUpperCase()}
                            </Typography>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                              <Typography variant="h6">
                                {metric.value.toFixed(2)}
                              </Typography>
                              <Chip
                                label={`${metric.benchmark_percentile.toFixed(1)}%`}
                                color={metric.benchmark_percentile >= 75 ? 'success' : metric.benchmark_percentile >= 50 ? 'warning' : 'error'}
                                size="small"
                              />
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={metric.benchmark_percentile}
                              sx={{ mb: 1 }}
                            />
                            <Typography variant="caption" color="textSecondary">
                              Improvement potential: {(100 - metric.benchmark_percentile).toFixed(1)}%
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </>
      )}

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Export Report</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Report Title"
                value={reportTitle}
                onChange={(e) => setReportTitle(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Export Format</InputLabel>
                <Select
                  value={selectedFormat}
                  onChange={(e) => setSelectedFormat(e.target.value)}
                  label="Export Format"
                >
                  {exportFormats.map((format) => (
                    <MenuItem key={format.value} value={format.value}>
                      {format.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Chart Type</InputLabel>
                <Select
                  value={selectedChartType}
                  onChange={(e) => setSelectedChartType(e.target.value)}
                  label="Chart Type"
                >
                  {chartTypes.map((chart) => (
                    <MenuItem key={chart.value} value={chart.value}>
                      {chart.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeCharts}
                    onChange={(e) => setIncludeCharts(e.target.checked)}
                  />
                }
                label="Include Charts"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeSummary}
                    onChange={(e) => setIncludeSummary(e.target.checked)}
                  />
                }
                label="Include Summary"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleExport}
            variant="contained"
            disabled={exporting}
            startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
          >
            {exporting ? 'Exporting...' : 'Export'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={scheduleDialogOpen} onClose={() => setScheduleDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Schedule Report</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Report Name"
                value={scheduleConfig.name}
                onChange={(e) => setScheduleConfig({ ...scheduleConfig, name: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={scheduleConfig.frequency}
                  onChange={(e) => setScheduleConfig({ ...scheduleConfig, frequency: e.target.value })}
                  label="Frequency"
                >
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Recipients (comma-separated emails)"
                value={scheduleConfig.recipients}
                onChange={(e) => setScheduleConfig({ ...scheduleConfig, recipients: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Format</InputLabel>
                <Select
                  value={scheduleConfig.format}
                  onChange={(e) => setScheduleConfig({ ...scheduleConfig, format: e.target.value })}
                  label="Format"
                >
                  {exportFormats.map((format) => (
                    <MenuItem key={format.value} value={format.value}>
                      {format.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleScheduleReport}
            variant="contained"
            startIcon={<ScheduleIcon />}
          >
            Schedule Report
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdvancedReportsDashboard;
